import { useState, type Dispatch, type SetStateAction } from 'react';
import { useDesignSuggestions } from '~/hooks/mutations/use-coding-mutations';
import { suggestGateState } from './design-direction-gate';
import { applySuggestionBatch, type DesignDirectionDraft, type DesignSuggestion } from './design-direction';

/**
 * FRONT-2382 review: `DesignDirectionDisclosure`'s "Let EDDY suggest"
 * request/pick state — the fetched suggestions, the suggest-gate, and
 * toggling a pick on the shared draft — pulled out into its own hook so
 * that component is rendering only. Logic unchanged from the original
 * inline version.
 */
export function useDesignSuggestionPicks({
  name,
  context,
  value,
  onChange,
}: {
  name: string;
  context: string;
  value: DesignDirectionDraft;
  onChange: Dispatch<SetStateAction<DesignDirectionDraft>>;
}) {
  const [suggestions, setSuggestions] = useState<DesignSuggestion[]>([]);
  const [hasSuggested, setHasSuggested] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const suggest = useDesignSuggestions();
  const suggestGate = suggestGateState({ name, context, suggesting: suggest.isPending });

  function onSuggest() {
    suggest.mutate(
      {
        name,
        context,
        inspiration: value.inspiration,
        seenTitles: suggestions.map((s) => s.title),
        seenImageUrls: suggestions.map((s) => s.imageUrl),
      },
      {
        onSuccess: (data) => {
          const next = applySuggestionBatch({
            prevSuggestions: suggestions,
            prevHasSuggested: hasSuggested,
            batch: data.suggestions,
          });
          setSuggestions(next.suggestions);
          setHasSuggested(next.hasSuggested);
          setSuggestError(next.error);
        },
        onError: (err) => setSuggestError((err as Error).message || 'Could not get suggestions right now.'),
      },
    );
  }

  function onTogglePick(suggestion: DesignSuggestion) {
    onChange((prev) => {
      const picked = prev.picks.some((p) => p.id === suggestion.id);
      return {
        ...prev,
        picks: picked
          ? prev.picks.filter((p) => p.id !== suggestion.id)
          : [
              ...prev.picks,
              {
                id: suggestion.id,
                title: suggestion.title,
                description: suggestion.description,
                imageUrl: suggestion.imageUrl,
              },
            ],
      };
    });
  }

  function isPicked(id: string) {
    return value.picks.some((p) => p.id === id);
  }

  return {
    hasSuggested,
    suggestGate,
    suggesting: suggest.isPending,
    suggestError,
    suggestions,
    isPicked,
    onSuggest,
    onTogglePick,
  };
}
